import React, { useCallback, useMemo, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";

import AssistantPhotoOutlinedIcon from "@mui/icons-material/AssistantPhotoOutlined";
import DataSaverOnOutlinedIcon from "@mui/icons-material/DataSaverOnOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import { Avatar, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { Menu } from "components/popups";
import { Rename } from "components/Prompts";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Routes } from "constants/routes";
import { useModals, usePrompt, useUrlHooks } from "hooks";
import { useReportsQueriesSelector, useUserActions, useUserSelector } from "store";
import { IBookmarkType, IBookmarkUser, IReportType, UrlQueryParams } from "store/types";

import { RoleName } from "../../constants";

interface Props {
  title: string;
  isActive?: boolean;
  onClick: () => void;
  id: number;
  isDefault: boolean;
  allowSetDefault?: boolean;
  isPrivate: boolean;
  ownerData: IBookmarkUser;
  icon?: React.ReactNode;
  isAccountDefault?: boolean;
  bookmarkType?: IBookmarkType;
}

export const GroupItem: React.FC<Props> = ({
  id,
  title,
  onClick,
  ownerData,
  isActive,
  isDefault,
  isPrivate,
  allowSetDefault = false,
  icon,
  isAccountDefault,
  bookmarkType
}) => {
  const openPrompt = usePrompt();
  const { updateBookmarkAction, deleteBookmarkAction, getBookmarksAction } = useUserActions();
  const containerRef = useRef<HTMLButtonElement>(null);
  const { errorModal } = useModals();
  const queries = useReportsQueriesSelector();
  const { pathname, search } = useLocation();
  const { queryParams } = useUrlHooks();
  const user = useUserSelector();

  const bookmarkReportRoutes = [Routes.dealReport, Routes.snapshot, Routes.timeline, Routes.classic];
  const isOwnBookmark = user.id === ownerData.id;

  const currentReportPage = useMemo(() => {
    if (pathname === Routes.snapshot) return IReportType.PORTFOLIO;
    if (pathname === Routes.timeline) return IReportType.PORTFOLIO;
    if (pathname === Routes.comparison) return IReportType.COMPARISON;
    if (pathname === Routes.classic) return IReportType.PORTFOLIO;
    if (pathname && matchPath(pathname, Routes.dealReport)) return IReportType.DEAL_VIEW;
  }, [pathname]);

  const currentItemTypeForOverride = useMemo(() => {
    if (matchPath(pathname, Routes.myDeals)) {
      return {
        target: "deals",
        type: "deal"
      };
    }
    if (bookmarkReportRoutes.some((route) => matchPath(pathname, route))) {
      return {
        target: "reports",
        type: "report"
      };
    }
    if (matchPath(pathname, Routes.monitoring)) {
      return {
        target: "dataOperations",
        type: "data_operations"
      };
    }
    return null;
  }, [pathname]);

  const isBookmarkTypeMatchingCurrentPage = useMemo(() => {
    if (!bookmarkType || !currentItemTypeForOverride) return false;

    return bookmarkType === currentItemTypeForOverride.type;
  }, [bookmarkType, currentItemTypeForOverride]);

  const onRename = useCallback(async () => {
    try {
      const result = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorEl: containerRef.current as HTMLElement,
        props: {
          title
        }
      });
      if (result) {
        await updateBookmarkAction({ name: result, id });
      }
    } catch (_error) {
      errorModal({ msg: "Something when wrong" });
    }
  }, [id, title, openPrompt, updateBookmarkAction, errorModal]);

  const onSetDefault = useCallback(async () => {
    try {
      await updateBookmarkAction({ id, is_default: !isDefault });
    } catch (_error) {
      errorModal({ msg: "Something when wrong" });
    }
  }, [id, isDefault]);

  const onOverrideBookmark = useCallback(async () => {
    try {
      if (currentItemTypeForOverride?.type === "data_operations") {
        await updateBookmarkAction({ id, params: pathname + (search || "") });
        return;
      }

      let resultQuery: string = "";
      if (currentItemTypeForOverride?.type === "report") {
        resultQuery = JSON.stringify(queries);
      } else {
        const target = currentItemTypeForOverride?.target as keyof UrlQueryParams;
        const valueToSave = queryParams[target];
        if (valueToSave) {
          const queryObject = JSON.parse(valueToSave as string);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { deal, ...rest } = queryObject; //WE DON'T NEED TO SAVE DEAL IN URL OF BOOKMARK
          resultQuery = JSON.stringify(rest);
        }
      }
      if (resultQuery) {
        await updateBookmarkAction({
          id,
          params: pathname + `?${currentItemTypeForOverride?.target}=` + encodeURIComponent(resultQuery),
          report_type:
            typeof currentItemTypeForOverride === "object" && currentItemTypeForOverride?.type === "report"
              ? currentReportPage
              : undefined
        });
      }
    } catch (_error) {
      errorModal({ msg: "Something when wrong" });
    }
  }, [queries, queryParams, pathname, search, id, currentItemTypeForOverride, currentReportPage]);

  const onDelete = useCallback(async () => {
    try {
      await deleteBookmarkAction(id);
    } catch (_error) {
      errorModal({ msg: "Something when wrong" });
    }
  }, [id]);

  const menuItems = [
    {
      title: isDefault ? "Unset Default" : "Set Default",
      icon: <AssistantPhotoOutlinedIcon />,
      onClick: onSetDefault,
      hidden: !allowSetDefault
    },
    {
      title: "Save View",
      icon: <DataSaverOnOutlinedIcon />,
      onClick: onOverrideBookmark,
      hidden: !isOwnBookmark || !isBookmarkTypeMatchingCurrentPage
    },
    {
      title: "Rename",
      icon: <TitleOutlinedIcon />,
      onClick: onRename,
      hidden: !isOwnBookmark
    },
    {
      title: "Delete",
      icon: <DeleteOutlineIcon />,
      onClick: onDelete,
      hidden: !isOwnBookmark
    }
  ];
  return (
    <>
      <Grid
        container
        spacing={2}
        display="flex"
        alignItems="center"
        sx={{ color: "#0a3142", padding: "4px 0", fontWeight: 500, fontSize: 15 }}
      >
        <Grid item xs={2}>
          {icon}
        </Grid>
        <Grid item xs={7} display="flex" alignItems="center">
          <MaterialTooltip placement="right" isShow title={title}>
            <Typography
              onClick={onClick}
              sx={{
                height: "fit-content",
                width: "fit-content",
                fontSize: "15px",
                transition: "color 0.1s linear",
                cursor: "pointer",
                color: isActive ? "#26a6ae" : "#0a3142",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {title}
            </Typography>
          </MaterialTooltip>
        </Grid>
        <Grid item xs={3}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="5px">
            {isDefault && <AssistantPhotoOutlinedIcon />}
            {!isOwnBookmark && (
              <Tooltip placement="bottom" title={ownerData?.full_name ?? ""}>
                <Avatar
                  sx={{ width: 24, height: 24 }}
                  src={ownerData?.profile_photo_url}
                  imgProps={{ decoding: "async" }}
                />
              </Tooltip>
            )}
            {isOwnBookmark && (
              <Menu
                icon={
                  isPrivate ? (
                    <LockOutlinedIcon color="primary" sx={{ width: "20px", height: "20px" }} />
                  ) : (
                    <PublicOutlinedIcon color="primary" sx={{ width: "20px", height: "20px" }} />
                  )
                }
                options={[
                  {
                    title: "Only Me",
                    icon: <LockOutlinedIcon />,
                    onClick: async () => {
                      if (isPrivate) return;
                      await updateBookmarkAction({ id, is_private: true });
                    }
                  },
                  {
                    title: "Share With My Team",
                    icon: <PublicOutlinedIcon />,
                    onClick: async () => {
                      if (!isPrivate) return;
                      await updateBookmarkAction({ id, is_private: false });
                    }
                  },
                  {
                    title: isAccountDefault ? "Unset Account Default" : "Set Account Default",
                    icon: <AssistantPhotoOutlinedIcon />,
                    onClick: async () => {
                      await updateBookmarkAction({ id, account_default: !isAccountDefault });
                      await getBookmarksAction();
                    },
                    hidden: !user.email.includes("@altius.capital") && user.role.name !== RoleName.Admin
                  }
                ]}
              />
            )}
            <Menu
              iconButtonProps={{ ref: containerRef }}
              options={menuItems}
              icon={<MoreVertIcon sx={{ color: "#0a3142", height: "20px", width: "20px" }} />}
            />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};
